/**
 * `RangeRunner`, orchestrates a single playback run over `[fromTick, toTick]`.
 *
 * Pure TS, zero dependency on React / Studio / Pixi / DOM. The runner
 * consumes a `RuntimeBridge` adapter which abstracts the concrete
 * Studio/Transport calls; the React hook bridge (chantier 7) provides
 * the concrete implementation.
 *
 * Run lifecycle
 * -------------
 *   1. `setPlaybackRate(rate)`          , applied **before** count-in
 *      so the count-in audibly matches the play tempo.
 *   2. `seek(fromTick)`                 , playhead positioned.
 *   3. `start` or `startWithCountIn`    , depending on `opts.withCountIn`.
 *   4. `onTick` subscription            , installed once playback has begun.
 *      The callback forwards live ticks to `opts.onTick?` and triggers
 *      `pause()` + resolution as soon as `tick >= toTick`.
 *
 * Concurrency policy
 * ------------------
 * Only one run can be in flight at a time. Calling `run` while another
 * run is active throws a `RangeRunnerBusyError`. This is intentional:
 * queueing would silently delay user actions and complicate the
 * `cancel()` contract. The caller is expected to call `cancel()` first
 * if it wants to start a new run.
 *
 * Cancellation semantics
 * ----------------------
 * `cancel()` settles the in-flight run with `{ kind: 'cancelled' }`,
 * regardless of which step it was on:
 *
 *   - During count-in: invokes `bridge.cancelCountIn?()`. If the bridge
 *     does not implement cancellation, the runner still resolves with
 *     `cancelled` immediately and silently ignores the eventual
 *     `start*` resolution (no tick listener is ever attached).
 *   - During playback: invokes `bridge.pause()` and unsubscribes the
 *     tick listener.
 *
 * Calling `cancel()` outside of a run is a no-op.
 *
 * Playback rate restoration
 * -------------------------
 * The runner does **not** restore `playbackRate` to `1` after a run.
 * Successive runs in the same phase often share a rate (e.g. S4 stays
 * at 60% across retries), and resetting it between runs would force
 * the caller to re-set it every time. The caller owns the rate.
 *
 * The watchdog
 * ------------
 * A run can stop making progress without any error being raised. The
 * founding case: on an iPad brought back after two hours with the screen
 * locked, Safari has put the `AudioContext` in `interrupted`, its
 * `currentTime` is frozen, and the count-in — which pre-computes its
 * beat times on that clock — lights its first beat and waits for a
 * second that never comes. `startWithCountIn` never resolves, the tick
 * stream never starts, and the run sits in flight until the page is
 * reloaded. Nothing measured it, because nothing failed.
 *
 * So the runner watches its own progress on the wall clock. During the
 * count-in, progress is the audio clock advancing (read through
 * `bridge.probe`); during playback, it is the tick moving — read from
 * the probe when there is one, so a background tab whose
 * `requestAnimationFrame` has stopped is not mistaken for a stuck run,
 * and from the tick stream otherwise. `stallAfterMs` without progress
 * settles the run with `{ kind: 'error', error: RunStalledError }`, after
 * cancelling the count-in or pausing playback, so the caller can say
 * something and measure it.
 */

import type { RuntimeBridge, RuntimeProbe } from './runtime-bridge';

// ── Public types ─────────────────────────────────────────────────

/** Options passed to a single `run()` call. */
export type RangeRunOptions = {
  /** First tick of the range (inclusive). */
  readonly fromTick: number;
  /**
   * Last tick of the range. Playback is paused as soon as the polled
   * tick is `>= toTick`. Must be strictly greater than `fromTick`,
   * otherwise the run rejects with a clear error.
   */
  readonly toTick: number;
  /**
   * Playback rate (`1` = nominal). Applied before count-in so the
   * count-in audibly matches the play tempo. The runner does NOT
   * restore the rate after the run, successive runs in the same
   * phase share a rate.
   */
  readonly rate: number;
  /**
   * `true`  → uses `bridge.startWithCountIn` (audible count-in then play).
   * `false` → uses `bridge.start` directly (immediate playback).
   *
   * S4/S5/phase-2 runs use `true`; S1b/S2b/S3 practice runs use `false`.
   */
  readonly withCountIn: boolean;
  /**
   * Optional live tick callback. Invoked on every tick update from
   * the bridge while playback is in progress. Useful for the UI to
   * reflect playhead position, or for the orchestrator to dispatch
   * tick-based detector queries.
   *
   * The runner forwards ticks **as received** from the bridge, no
   * throttling, no clamping. Callers that need a stop-tick callback
   * should rely on the `RunOutcome` instead.
   */
  readonly onTick?: (tick: number) => void;
};

/** Outcome of a single run. */
export type RunOutcome =
  | {
      readonly kind: 'completed';
      readonly startedAtMs: number;
      readonly endedAtMs: number;
    }
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'error'; readonly error: unknown };

/** Thrown when `run()` is called while another run is in flight. */
export class RangeRunnerBusyError extends Error {
  constructor() {
    super('RangeRunner: a run is already in progress. Cancel it first.');
    this.name = 'RangeRunnerBusyError';
  }
}

/** Where a run was when it stopped making progress. */
export type RunPhase = 'count_in' | 'playback';

/**
 * The error a run settles with when its clocks stop. Carries what the
 * watchdog saw, so the caller can report it without probing again.
 */
export class RunStalledError extends Error {
  readonly phase: RunPhase;
  /** How long the run went without progress before being settled, in ms. */
  readonly stalledForMs: number;
  /** `AudioContext.state` at the time, or `'unknown'` without a probe. */
  readonly audioState: string;
  /** Whether the audio clock was still advancing while the run was stuck. */
  readonly audioMoving: boolean;

  constructor(detail: {
    readonly phase: RunPhase;
    readonly stalledForMs: number;
    readonly audioState: string;
    readonly audioMoving: boolean;
  }) {
    super(
      `RangeRunner: no progress for ${Math.round(detail.stalledForMs)} ms during ${detail.phase} (audio ${detail.audioState}).`,
    );
    this.name = 'RunStalledError';
    this.phase = detail.phase;
    this.stalledForMs = detail.stalledForMs;
    this.audioState = detail.audioState;
    this.audioMoving = detail.audioMoving;
  }
}

/** Tuning of the watchdog. See the module header. */
export type RangeRunnerOptions = {
  /**
   * Time without progress after which a run is settled as stalled.
   * Default 1500 ms: a running audio clock advances every render quantum,
   * and a playhead every ~25 ms, so anything close to a second is already
   * a frozen clock, not a slow device.
   */
  readonly stallAfterMs?: number;
  /** How often the watchdog looks. Default 250 ms. */
  readonly watchEveryMs?: number;
};

const DEFAULT_STALL_AFTER_MS = 1500;
const DEFAULT_WATCH_EVERY_MS = 250;

// ── Implementation ───────────────────────────────────────────────

type ActiveRun = {
  readonly options: RangeRunOptions;
  readonly resolve: (outcome: RunOutcome) => void;
  readonly startedAtMs: number;
  cancelled: boolean;
  unsubscribeTick: (() => void) | null;
  phase: RunPhase;
  /** Wall time of the last observed progress. */
  lastProgressAtMs: number;
  /** Last readings, to tell progress from repetition. */
  lastProbe: RuntimeProbe | null;
  lastTick: number | null;
  /** Whether the audio clock moved during the last watch interval. */
  audioMoving: boolean;
  watchdogId: ReturnType<typeof setTimeout> | null;
};

export class RangeRunner {
  private active: ActiveRun | null = null;
  private readonly stallAfterMs: number;
  private readonly watchEveryMs: number;

  constructor(
    private readonly bridge: RuntimeBridge,
    options: RangeRunnerOptions = {},
  ) {
    this.stallAfterMs = options.stallAfterMs ?? DEFAULT_STALL_AFTER_MS;
    this.watchEveryMs = options.watchEveryMs ?? DEFAULT_WATCH_EVERY_MS;
  }

  /** True while a run is in progress. Useful for UI guards / tests. */
  isRunning(): boolean {
    return this.active !== null;
  }

  /**
   * Start a run on `[fromTick, toTick]`. Returns a promise that resolves
   * with the `RunOutcome` (`completed`, `cancelled`, or `error`). Throws
   * `RangeRunnerBusyError` if another run is in flight.
   */
  run(options: RangeRunOptions): Promise<RunOutcome> {
    if (this.active !== null) {
      throw new RangeRunnerBusyError();
    }

    if (options.toTick <= options.fromTick) {
      return Promise.resolve({
        kind: 'error',
        error: new Error(
          `RangeRunner: toTick (${options.toTick}) must be greater than fromTick (${options.fromTick}).`,
        ),
      });
    }

    return new Promise<RunOutcome>((resolve) => {
      const startedAtMs = now();
      const active: ActiveRun = {
        options,
        resolve,
        startedAtMs,
        cancelled: false,
        unsubscribeTick: null,
        phase: options.withCountIn ? 'count_in' : 'playback',
        lastProgressAtMs: startedAtMs,
        lastProbe: null,
        lastTick: null,
        audioMoving: false,
        watchdogId: null,
      };

      this.active = active;

      // Order matters: rate must be applied BEFORE count-in, otherwise
      // the count-in audibly fires at the wrong tempo.
      try {
        this.bridge.setPlaybackRate(options.rate);
        this.bridge.seek(options.fromTick);
      } catch (error) {
        this.settle(active, { kind: 'error', error });

        return;
      }

      this.scheduleWatch(active);

      if (options.withCountIn) {
        this.runWithCountIn(active);

        return;
      }

      this.runWithoutCountIn(active);
    });
  }

  /**
   * Cancel the in-flight run, if any. No-op if no run is active. The
   * pending promise will settle with `{ kind: 'cancelled' }`.
   */
  cancel(): void {
    const active = this.active;
    if (active === null) {
      return;
    }

    if (active.cancelled) {
      return;
    }

    this.abort(active, { kind: 'cancelled' });
  }

  // ── Internals ──────────────────────────────────────────────────

  /**
   * Stop whatever the run was doing and settle it with `outcome`. Shared
   * by an external `cancel()` and by the watchdog: both interrupt a run
   * from outside its own flow, and both must leave the runtime paused.
   */
  private abort(active: ActiveRun, outcome: RunOutcome): void {
    active.cancelled = true;

    // Try to interrupt the count-in if it's still running.
    if (this.bridge.cancelCountIn !== undefined) {
      try {
        this.bridge.cancelCountIn();
      } catch {
        // Swallow, cancellation is best-effort. The run is already
        // marked cancelled and will settle accordingly.
      }
    }

    // If we already subscribed to ticks, playback has started, pause it.
    if (active.unsubscribeTick !== null) {
      try {
        this.bridge.pause();
      } catch {
        // Same as above: best-effort.
      }

      active.unsubscribeTick();
      active.unsubscribeTick = null;
    }

    this.settle(active, outcome);
  }

  // ── Watchdog ───────────────────────────────────────────────────

  private scheduleWatch(active: ActiveRun): void {
    const id = setTimeout(() => {
      active.watchdogId = null;
      this.watch(active);
    }, this.watchEveryMs);

    // A run nobody settles — a test that walks away from it — must not keep
    // the process alive on its own. Browsers have no such notion and ignore
    // this; Node lets the timer be dropped with the process.
    if (typeof id === 'object' && id !== null && 'unref' in id) {
      (id as { unref: () => void }).unref();
    }

    active.watchdogId = id;
  }

  private watch(active: ActiveRun): void {
    if (this.active !== active || active.cancelled) {
      return;
    }

    const at = now();
    const probe = this.readProbe();

    if (probe !== null) {
      const previous = active.lastProbe;
      active.lastProbe = probe;

      if (previous !== null) {
        active.audioMoving = probe.audioTime > previous.audioTime;

        const progressed =
          active.phase === 'count_in'
            ? active.audioMoving
            : probe.tick !== previous.tick;

        if (progressed) {
          active.lastProgressAtMs = at;
        }
      } else {
        // First reading: nothing to compare with yet, and the run has
        // only just been armed.
        active.lastProgressAtMs = at;
      }
    }

    const stalledForMs = at - active.lastProgressAtMs;

    // Without a probe the count-in cannot be observed at all: its promise
    // is the only signal, and it is precisely the one that never comes.
    // Playback is still watched through the tick stream.
    const observable = probe !== null || active.phase === 'playback';

    if (observable && stalledForMs >= this.stallAfterMs) {
      this.abort(active, {
        kind: 'error',
        error: new RunStalledError({
          phase: active.phase,
          stalledForMs,
          audioState: probe?.audioState ?? 'unknown',
          audioMoving: active.audioMoving,
        }),
      });

      return;
    }

    this.scheduleWatch(active);
  }

  private readProbe(): RuntimeProbe | null {
    if (this.bridge.probe === undefined) {
      return null;
    }

    try {
      return this.bridge.probe();
    } catch {
      // A probe that throws is a probe that is not there.
      return null;
    }
  }

  /** Progress seen through the tick stream, for bridges without a probe. */
  private noteTick(active: ActiveRun, tick: number): void {
    if (active.lastTick !== tick) {
      active.lastTick = tick;
      active.lastProgressAtMs = now();
    }
  }

  private runWithCountIn(active: ActiveRun): void {
    let promise: Promise<void>;
    try {
      promise = Promise.resolve(
        this.bridge.startWithCountIn(active.options.fromTick, {
          skipSectionTips: true,
        }),
      );
    } catch (error) {
      this.settle(active, { kind: 'error', error });

      return;
    }

    promise.then(
      () => {
        // Cancellation may have arrived during the count-in.
        if (active.cancelled) {
          return;
        }

        this.attachTickMonitor(active);
      },
      (error) => {
        if (active.cancelled) {
          // The bridge resolved/rejected the count-in promise as part
          // of cancellation, outcome was already settled in cancel().
          return;
        }

        this.settle(active, { kind: 'error', error });
      },
    );
  }

  private runWithoutCountIn(active: ActiveRun): void {
    try {
      this.bridge.start(active.options.fromTick);
    } catch (error) {
      this.settle(active, { kind: 'error', error });

      return;
    }

    this.attachTickMonitor(active);
  }

  private attachTickMonitor(active: ActiveRun): void {
    const { toTick, onTick } = active.options;

    // Playback begins here: the count-in's progress no longer counts, the
    // playhead's does, and it gets a fresh allowance to start moving.
    active.phase = 'playback';
    active.lastProgressAtMs = now();

    const unsubscribe = this.bridge.onTick((tick) => {
      if (active.cancelled) {
        return;
      }

      this.noteTick(active, tick);

      if (onTick !== undefined) {
        onTick(tick);
      }

      if (tick >= toTick) {
        this.completeRun(active);
      }
    });

    // The cancel() call may have arrived between `attachTickMonitor`
    // entry and `onTick` subscription, check once more.
    if (active.cancelled) {
      unsubscribe();

      return;
    }

    active.unsubscribeTick = unsubscribe;
  }

  private completeRun(active: ActiveRun): void {
    if (active.unsubscribeTick !== null) {
      active.unsubscribeTick();
      active.unsubscribeTick = null;
    }

    try {
      this.bridge.pause();
    } catch (error) {
      this.settle(active, { kind: 'error', error });

      return;
    }

    this.settle(active, {
      kind: 'completed',
      startedAtMs: active.startedAtMs,
      endedAtMs: now(),
    });
  }

  private settle(active: ActiveRun, outcome: RunOutcome): void {
    // Defensive: the run may already have been settled (race between
    // a tick crossing toTick and an external cancel()).
    if (this.active !== active) {
      return;
    }

    if (active.watchdogId !== null) {
      clearTimeout(active.watchdogId);
      active.watchdogId = null;
    }

    this.active = null;
    active.resolve(outcome);
  }
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Wall-clock millisecond timestamp. Indirected through a function so
 * tests can swap it (Jest fake timers / Date.now mocking) without
 * patching `performance` globally.
 */
function now(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
}
